import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import {
  PERMISSION_DESCRIPTIONS,
  PERMISSION_LABELS,
  isPermissionForbiddenForRole,
  type Permission,
  type Role,
} from "@xop/dashboard-shared";
import type { JSX } from "react";

import { GROUPS } from "./permissionGroups.js";

/**
 * The section-grouped permission checkbox grid for one role. Each cell is the
 * human label + the raw permission key + a hover description. Permissions
 * structurally barred from the role (`isPermissionForbiddenForRole`) render
 * disabled + forced-unchecked so the matrix never lies. Extracted from
 * `RolesPanel.tsx`.
 */
export function PermissionGrid({
  role,
  draft,
  disabled,
  onToggle,
}: {
  role: Role;
  draft: Set<Permission>;
  disabled: boolean;
  onToggle: (perm: Permission, checked: boolean) => void;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 1.5,
        gridTemplateColumns: { sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
      }}
    >
      {GROUPS.map((group) => (
        <Box
          key={group.key}
          sx={{
            borderRadius: 1,
            border: 1,
            borderColor: "divider",
            bgcolor: "background.default",
            p: 1.25,
          }}
        >
          <Typography
            sx={{
              mb: 0.75,
              fontSize: 10,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
              color: "text.disabled",
            }}
          >
            {group.label}
          </Typography>
          <Box sx={{ display: "grid", gap: 0.5 }}>
            {group.permissions.map((perm) => {
              // Some permissions are structurally barred from a role (e.g.
              // sales_manager can never hold dashboard:view — the company pulse
              // has nothing for it; it lands on CRM). The toggle is disabled +
              // forced unchecked so an admin can't re-grant it.
              const forbidden = isPermissionForbiddenForRole(role, perm);
              const cellDisabled = disabled || forbidden;
              return (
                <FormControlLabel
                  key={perm}
                  data-testid={`role-perm-${role}-${perm}`}
                  disabled={cellDisabled}
                  title={forbidden ? "Not available for this role" : PERMISSION_DESCRIPTIONS[perm]}
                  sx={{
                    m: 0,
                    gap: 1,
                    alignItems: "flex-start",
                    borderRadius: 0.5,
                    px: 0.5,
                    py: 0.5,
                    ...(cellDisabled ? { opacity: 0.6 } : { "&:hover": { bgcolor: "surfaceSoft" } }),
                  }}
                  control={
                    <Checkbox
                      size="small"
                      sx={{ p: 0, mt: 0.25 }}
                      checked={draft.has(perm) && !forbidden}
                      disabled={cellDisabled}
                      onChange={(e) => onToggle(perm, e.target.checked)}
                    />
                  }
                  label={
                    // Human-readable label first (so a checkbox is never a
                    // cryptic suffix), with the raw permission key beneath in
                    // tiny mono for power users. Full sentence is the hover
                    // tooltip (title) above.
                    <Box component="span" sx={{ display: "block", minWidth: 0 }}>
                      <Box component="span" sx={{ display: "block", fontSize: 12, color: "text.primary", lineHeight: 1.3 }}>
                        {PERMISSION_LABELS[perm]}
                      </Box>
                      <Box component="span" sx={{ display: "block", fontFamily: "ui-monospace, monospace", fontSize: 10, color: "text.disabled" }}>
                        {perm}
                      </Box>
                    </Box>
                  }
                />
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
